import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

export default function BidWritingPane() {
  const [licitaciones, setLicitaciones] = useState([]);
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState("");
  const [seccion, setSeccion] = useState("resumen_ejecutivo");
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [ctxPreview, setCtxPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/licitaciones`).then(r => setLicitaciones(r.data || []));
    axios.get(`${API}/tender360/bid-writing/sections`).then(r => setSections(r.data || []));
  }, []);

  useEffect(() => {
    if (!selected) { setDrafts([]); setCurrentDraft(null); setCtxPreview(null); return; }
    axios.get(`${API}/tender360/bid-writing/${selected}/drafts`)
      .then(r => { setDrafts(r.data || []); setCurrentDraft(r.data?.[0] || null); })
      .catch(() => {});
    setCtxPreview(null);
    axios.get(`${API}/tender360/bid-writing/${selected}/context-preview`)
      .then(r => setCtxPreview(r.data)).catch(() => {});
  }, [selected]);

  const generate = async () => {
    if (!selected) { toast.error("Selecciona una licitación"); return; }
    setLoading(true);
    try {
      const draft = await postAiJob(`${API}/tender360/bid-writing/${selected}`, {
        seccion, extra_instructions: extra,
      });
      setCurrentDraft(draft);
      const dr = await axios.get(`${API}/tender360/bid-writing/${selected}/drafts`);
      setDrafts(dr.data || []);
      toast.success("Draft generado");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al generar");
    } finally { setLoading(false); }
  };

  const approve = async (approved) => {
    if (!currentDraft) return;
    try {
      const r = await axios.put(`${API}/tender360/bid-writing/drafts/${currentDraft.id}/approve`, {
        human_approved: approved, human_notes: "",
      });
      setCurrentDraft(r.data);
      toast.success(approved ? "Draft aprobado" : "Aprobación retirada");
    } catch { toast.error("Error al actualizar"); }
  };

  const copyToClipboard = () => {
    if (!currentDraft) return;
    navigator.clipboard.writeText(currentDraft.content);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="space-y-4" data-testid="writing-pane">
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-[#007934]"/> Redactor Pando con oversight humano obligatorio</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Licitación</label>
            <select value={selected} onChange={e => setSelected(e.target.value)}
                    data-testid="writing-licitacion-select"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white">
              <option value="">— Elegir —</option>
              {licitaciones.map(l => {
                const label = `${l.municipio || "?"} · ${(l.titulo || "").slice(0, 80)}`;
                return <option key={l.id} value={l.id}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Sección a redactar</label>
            <select value={seccion} onChange={e => setSeccion(e.target.value)}
                    data-testid="writing-section-select"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white">
              {sections.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
            </select>
            {sections.find(s => s.key === seccion) && (
              <p className="text-[10px] text-gray-400 mt-1">{sections.find(s => s.key === seccion).description}</p>
            )}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1 block">Instrucciones adicionales</label>
          <textarea value={extra} onChange={e => setExtra(e.target.value)}
                    placeholder="Ej: enfatizar experiencia en Madrid Este y sostenibilidad..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white resize-y"/>
        </div>
        {/* CC05 · Previsualización del contexto que usará la IA (antes de generar) */}
        {selected && ctxPreview && (
          <div className="border border-sky-200 bg-sky-50/70 rounded-lg p-3" data-testid="writing-context-preview">
            <div className="text-[10px] uppercase font-semibold text-sky-700 tracking-wide mb-1.5">Contexto que usará la IA</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {ctxPreview.fuentes.map((f, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]" data-testid={`writing-ctx-src-${i}`}>
                  <span className={f.present ? "text-emerald-600" : "text-amber-600"}>{f.present ? "✓" : "⚠"}</span>
                  <span className="text-gray-700"><b>{f.label}:</b> <span className="text-gray-500">{f.detail}</span></span>
                </div>
              ))}
            </div>
            {ctxPreview.faltan?.length > 0 && (
              <p className="text-[10px] text-amber-700 mt-1.5">Faltan: {ctxPreview.faltan.join(", ")} — la redacción será más genérica.</p>
            )}
            <p className="text-[10px] text-sky-700 mt-1">{ctxPreview.nota}</p>
          </div>
        )}
        <button onClick={generate} disabled={loading || !selected}
                data-testid="writing-generate-btn"
                className="bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
          {loading ? "Generando..." : "Generar draft Pando"}
        </button>
      </div>

      {currentDraft && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="draft-view">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3 gap-2">
            <div>
              <div className="text-sm font-bold text-gray-900">{currentDraft.seccion}</div>
              <div className="text-[10px] text-gray-400">
                {currentDraft.word_count} palabras · {new Date(currentDraft.generated_at).toLocaleString("es-ES")} · {currentDraft.model}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentDraft.human_approved
                ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded font-semibold flex items-center gap-1"><CheckCircle2 size={11}/> Aprobado humano</span>
                : <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded font-semibold">Pendiente de aprobación</span>}
              <button onClick={copyToClipboard} className="text-xs border border-gray-200 px-2 py-1 rounded hover:bg-gray-50 flex items-center gap-1" data-testid="draft-copy-btn">
                <Copy size={11}/> Copiar
              </button>
              {currentDraft.human_approved
                ? <button onClick={() => approve(false)} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200" data-testid="draft-unapprove-btn">Retirar aprobación</button>
                : <button onClick={() => approve(true)} className="text-xs bg-[#007934] text-white px-2 py-1 rounded hover:bg-[#006528]" data-testid="draft-approve-btn">Aprobar</button>
              }
            </div>
          </div>
          <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans leading-relaxed max-h-[70vh] overflow-y-auto p-3 bg-gray-50 rounded-lg" data-testid="draft-content">
{currentDraft.content}
          </pre>
        </div>
      )}

      {drafts.length > 1 && (
        <details className="bg-white rounded-xl border border-gray-200 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-gray-600">Otros drafts de esta licitación ({drafts.length})</summary>
          <div className="mt-2 space-y-1">
            {drafts.map(d => (
              <button key={d.id} onClick={() => setCurrentDraft(d)}
                      className="w-full text-left text-[11px] p-2 border border-gray-100 rounded hover:bg-gray-50 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{d.seccion}</span> · {d.word_count} pal.
                  {d.human_approved && <span className="ml-2 text-green-600">✓</span>}
                </div>
                <span className="text-gray-400">{new Date(d.generated_at).toLocaleString("es-ES")}</span>
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// M4: HISTORIC TENDER DATABASE
// ═══════════════════════════════════════════════════════════════════════════
